'use client';

// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  createMachine,
  state,
  state as final,
  state as initial,
  transition,
  reduce,
  guard,
  invoke,
  immediate,
} from 'robot3';
import { createUseMachine } from 'robot-hooks';
import { goBackReducer, toInterstitialReducer, saveCart } from './functions';
import { ACTION_TYPES, STATE } from './constants.js';
import {
  hasPetName,
  hasPetWeight,
  hasPetActivity,
  hasPetGender,
  hasPetBreeds,
  hasPetStatus,
  allPetsHaveProductInCart,
  submitCartFailed,
  allPetsComplete,
  isAddPup,
  hasInitialPetWeight,
  hasInitialPetActivity,
  isInitialFlow,
  isEditPet,
  hasPet,
  isDeletePet,
  initialPetComplete,
  notAddPup,
  notEditPet,
  hasMealPrep,
  showGiftOnboardingStep,
  showQuizAbandonerMenu,
  showPupBreedStep,
  skipPupBreedStep,
  isFromQuestion,
} from './guard';

export const useMachine = createUseMachine(useEffect, useState);

const createInitialContext = ({
  customer = {},
  pet = {},
  pet_id = '',
  recipes = { fresh: null },
  extras = [],
  planType = null,
  stateKey = STATE.initial,
  flags = {}, // This may come in handy moving forward with V3
} = {}) => ({
  customer,
  pet,
  pet_id,
  recipes,
  extras,
  planType,
  stateKey,
  flags,
});

export const onboardingMachine = createMachine(
  STATE.initial,
  {
    [STATE.initial]: initial(
      /**
       ** Transitions to Pup Name Page by default if
       ** - isAddPup: true
       ** - allPetsHaveProductInCart: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isAddPup),
        guard(allPetsHaveProductInCart),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.pupName,
          customer: payload.customer,
          pets: payload.pets,
          pet: null,
          pet_id: '',
          isAddPup: true,
          isEditPet: false,
        })),
      ),
      /**
       ** Transitions to Pup Name Page for edit pet flow if
       ** - isEditPet: true
       ** - initialPetComplete: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(isEditPet),
        guard(initialPetComplete),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          emailStepLast: payload.emailStepLast,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.pupName,
          customer: payload.customer,
          isEditPet: true,
          isAddPup: false,
          pets: payload.pets,
          pet:
            payload.pets.find(p => p.id === payload.pet_id) ?? payload.pets[0],
          pet_id: payload.pet_id,
        })),
      ),
      /**
       ** Transitions to Delete Pet state if
       ** - hasPet: true
       ** - isDeletePet: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.deletePet,
        guard(hasPet),
        guard(isDeletePet),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.deletePet,
          customer: payload.customer,
          pet_id: payload.pet_id,
          pet: payload.pets.find(p => p.id === payload.pet_id),
          deletePetFromCart: payload.deletePetFromCart,
        })),
      ),
      /**
       ** Transitions to the Quiz Abandoner Menu if
       ** - showQuizAbandonerMenu: true (Flagship Test variation)
       ** - hasPet: true (returning user with prior onboarding data)
       ** - notAddPup: true
       ** - notEditPet: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.quizAbandonerMenu,
        guard(showQuizAbandonerMenu),
        guard(hasPet),
        guard(notAddPup),
        guard(notEditPet),
        reduce((ctx, { payload }) => {
          const isEditPet = !!payload.isEditPet || !!ctx.isEditPet;

          return {
            ...ctx,
            stateKey: STATE.quizAbandonerMenu,
            customer: payload.customer,
            pets: payload.pets,
            pet:
              payload.pets?.find(p => p.id === payload.pet_id) ??
              payload.pet ??
              payload.pets?.[0],
            pet_id: payload.pet_id,
            cart: payload.cart,
            flags: { ...ctx.flags, ...(payload.flags || {}) },
            isEditPet: isEditPet,
            isAddPup: false,
          };
        }),
      ),
      /**
       ** Transitions to Pup Info Page if page by default if
       ** - hasPet: true
       ** - notAddPup: true
       ** Pup Info then takes over and passes the pet to the correct step via immediate transitions
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.pupName,
        guard(hasPet),
        guard(notAddPup),
        reduce((ctx, { payload }) => {
          const isEditPet = !!payload.isEditPet || !!ctx.isEditPet;

          return {
            ...ctx,
            stateKey: STATE.pupName,
            customer: payload.customer,
            pet:
              payload.pets?.find(p => p.id === payload.pet_id) ??
              payload.pet ??
              payload.pets?.[0],
            pet_id: payload.pet_id,
            flags: { ...ctx.flags, ...(payload.flags || {}) },
            isEditPet: isEditPet,
            isAddPup: false,
          };
        }),
      ),
      /**
       ** Transitions to Intro Page
       ** Only show this when you first enter onboarding if
       ** - isInitialFlow: true
       ** - notAddPup: true
       */
      transition(
        ACTION_TYPES.initialLoad,
        STATE.intro,
        guard(isInitialFlow),
        guard(notAddPup),
        reduce((ctx, { payload }) => ({
          ...ctx,
          flags: { ...ctx.flags, ...payload.flags },
          stateKey: STATE.intro,
          isAddPup: false,
          isEditPet: false,
        })),
      ),
    ),
    [STATE.intro]: state(
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.toPupName,
        STATE.pupName,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupName,
          pet: payload?.pet,
          pet_id: payload?.pet?.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.quizAbandonerMenu]: state(
      /**
       ** Route 1: Primary CTA beforeMealPrep bucket
       ** Route 2: Secondary CTA beforeMealPrep bucket, Secondary CTA afterMealPrep bucket
       ** - reviewQuiz: unset/false → auto-advance through to the first unfinished step
       ** - reviewQuiz: true → stay at Q1 with answers prefilled
       */
      transition(
        ACTION_TYPES.toPupName,
        STATE.pupName,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupName,
          initialFlow: !payload?.reviewQuiz,
          pet: payload?.pet ?? ctx.pet,
          pet_id: payload?.pet?.id ?? ctx.pet_id,
          flags: { ...ctx.flags, ...(payload?.flags || {}) },
        })),
      ),
      /**
       ** Route 1: Primary CTA afterMealPrep bucket
       ** Route 2: Secondary CTA atCheckout bucket
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          pet: payload?.pet ?? ctx.pet,
          pet_id: payload?.pet?.id ?? ctx.pet_id,
          flags: { ...ctx.flags, ...(payload?.flags || {}) },
        })),
      ),
      /**
       ** Route: Primary CTA atCheckout bucket
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          flags: { ...ctx.flags, ...(payload?.flags || {}) },
        })),
      ),
      /**
       ** Route: fallback
       */
      transition(
        ACTION_TYPES.toIntro,
        STATE.intro,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.intro,
          initialFlow: false,
          flags: { ...ctx.flags, ...(payload?.flags || {}) },
        })),
      ),
    ),
    [STATE.pupName]: state(
      /**
       ** Immediately transition to PupGender if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetName: true
       */
      immediate(
        STATE.pupGender,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetName),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupGender,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Intro Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.intro,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.intro,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.toPupGender,
        STATE.pupGender,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupGender,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.pupGender]: state(
      /**
       ** Immediately transition to PupBreed if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetGender: true
       ** - showPupBreedStep: true (A/B test variation)
       */
      immediate(
        STATE.pupBreed,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetGender),
        guard(showPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupBreed,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Immediately transition to PupStatus if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetGender: true
       ** - showPupBreedStep: false (control variation)
       */
      immediate(
        STATE.pupStatus,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetGender),
        guard(skipPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupName Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupName,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupName,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupBreed Page
       */
      transition(
        ACTION_TYPES.toPupBreed,
        STATE.pupBreed,
        guard(showPupBreedStep),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupBreed,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      /**
       ** Route: Continue/Next (control variation)
       **
       ** Transitions to PupStatus Page, skipping breed
       */
      transition(
        ACTION_TYPES.toPupStatus,
        STATE.pupStatus,
        guard(skipPupBreedStep),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.pupBreed]: state(
      /**
       ** Immediately transition to PupStatus if
       ** - showPupBreedStep: false (control variation)
       */
      immediate(
        STATE.pupStatus,
        guard(skipPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Immediately transition to PupStatus if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetBreeds: true
       */
      immediate(
        STATE.pupStatus,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetBreeds),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupGender,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupGender,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.toPupStatus,
        STATE.pupStatus,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.pupStatus,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.pupStatus]: state(
      /**
       ** Immediately transition to Weight Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasPetStatus: true
       */
      immediate(
        STATE.weight,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasPetStatus),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.weight,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button (test variation)
       *
       ** Transitions to PupBreed Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupBreed,
        guard(showPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupBreed,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Back Button (control variation)
       *
       ** Transitions to PupGender Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupGender,
        guard(skipPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupGender,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.toWeight,
        STATE.weight,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.weight,
          pet: payload.pet,
          pet_id: payload.pet.id,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.weight]: state(
      /**
       ** Immediately transition to Activity Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetWeight: true
       */
      immediate(
        STATE.activity,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetWeight),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.activity,
          pet_id: ctx?.pet?.id,
          initialFlow: true,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to PupStatus Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupStatus,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.pupStatus,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Activity Selection Page if
       ** - hasPetWeight: true
       */
      transition(
        ACTION_TYPES.toActivity,
        STATE.activity,
        guard(hasPetWeight),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.activity,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.activity]: state(
      /**
       ** Immediately transition to Allergies Page if
       ** - isInitialFlow: true
       ** - notAddPup: true
       ** - hasInitialPetActivity: true
       */
      immediate(
        STATE.allergies,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasInitialPetActivity),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.allergies,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Weight Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.weight,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.weight,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Allergies Page if
       ** - hasPetActivity: true
       */
      transition(
        ACTION_TYPES.toAllergies,
        STATE.allergies,
        guard(hasPetActivity),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.allergies,
          isEditPet: ctx.isEditPet,
          pet: payload.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.allergies]: state(
      /**
       ** Immediately transition to Recommended Plan if
       ** - notAddPup: true
       ** - hasMealPrep: true
       */
      immediate(
        STATE.recommendedPlan,
        guard(isInitialFlow),
        guard(notAddPup),
        guard(hasMealPrep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          initialFlow: true,
          pet_id: ctx?.pet?.id,
        })),
      ),
      /**
       ** Route: Back Button
       *
       ** Transitions to Activity Selection Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.activity,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Recommended Plan Page if
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toRecommendedPlan,
        STATE.recommendedPlan,
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          pet: payload.pet?.id ? payload.pet : ctx.pet,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.toInterstitial,
        STATE.interstitial,
        reduce(toInterstitialReducer),
      ),
    ),
    [STATE.interstitial]: state(
      /**
       ** Continue — advance to the next quiz step based on fromQuestion
       */
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.pupGender,
        guard(isFromQuestion('pupName')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupGender,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.pupBreed,
        guard(isFromQuestion('pupGender')),
        guard(showPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupBreed,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.pupStatus,
        guard(isFromQuestion('pupGender')),
        guard(skipPupBreedStep),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupStatus,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.pupStatus,
        guard(isFromQuestion('pupBreed')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupStatus,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.weight,
        guard(isFromQuestion('pupStatus')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.weight,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.activity,
        guard(isFromQuestion('weight')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.activity,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.allergies,
        guard(isFromQuestion('activity')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.allergies,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      transition(
        ACTION_TYPES.continueFromInterstitial,
        STATE.recommendedPlan,
        guard(isFromQuestion('allergies')),
        guard(allPetsComplete),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.recommendedPlan,
          pet: ctx.pet,
          pet_id: ctx.pet?.id ?? ctx.pet_id,
        })),
      ),
      /**
       ** Back — return to the question that launched the interstitial
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.pupName,
        guard(isFromQuestion('pupName')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupName,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.pupGender,
        guard(isFromQuestion('pupGender')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupGender,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.pupBreed,
        guard(isFromQuestion('pupBreed')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupBreed,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.pupStatus,
        guard(isFromQuestion('pupStatus')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.pupStatus,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.weight,
        guard(isFromQuestion('weight')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.weight,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.activity,
        guard(isFromQuestion('activity')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.activity,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        guard(isFromQuestion('allergies')),
        reduce(ctx => ({
          ...ctx,
          fromQuestion: undefined,
          interstitial: undefined,
          stateKey: STATE.allergies,
          initialFlow: false,
        })),
        reduce(goBackReducer),
      ),
    ),
    [STATE.recommendedPlan]: state(
      /**
       ** Route: Back Button
       *
       ** Transitions to Allergies Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.allergies,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.allergies,
          planType: payload.planType ?? ctx.planType,
          flags: { ...ctx.flags, ...payload.flags },
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to submit cart function
       */
      transition(
        ACTION_TYPES.selectedFreshRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          recipes: { ...ctx.recipes, fresh: payload.recipes },
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
      transition(
        ACTION_TYPES.selectedMixedRecipes,
        STATE.submitCart,
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
          planType: payload.planType,
          recipes: { dry: payload.recipes.dry, fresh: payload.recipes.fresh },
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.submitCart]: invoke(
      saveCart,
      /**
       ** Transitions to recommended Plan Page if
       ** - submitCartFailed: true
       */
      transition(
        'done',
        STATE.recommendedPlan,
        guard(submitCartFailed),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.recommendedPlan,
        })),
      ),
      /**
       ** Transitions to Gift With Purchase Page
       ** if showGiftOnboardingStep flag is true
       */
      transition(
        'done',
        STATE.giftWithPurchase,
        guard(showGiftOnboardingStep),
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.giftWithPurchase,
        })),
      ),
      /**
       ** Transitions to Extras Page
       */
      transition(
        'done',
        STATE.extras,
        reduce((ctx, payload) => ({
          ...ctx,
          submitCartResult: payload.data,
          stateKey: STATE.extras,
        })),
      ),
    ),
    [STATE.giftWithPurchase]: state(
      /**
       ** Route: Back Button
       **
       ** Transitions to Recommended Plan Page
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       **
       ** Transitions to Extras Page
       */
      transition(
        ACTION_TYPES.toExtras,
        STATE.extras,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.extras,
        })),
      ),
    ),
    [STATE.extras]: state(
      /**
       ** Route: Back Button
       **
       ** Transitions to Gift With Purchase Page
       ** if showGiftOnboardingStep flag is true
       */
      transition(
        ACTION_TYPES.goBack,
        STATE.giftWithPurchase,
        guard(showGiftOnboardingStep),
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.giftWithPurchase,
        })),
        reduce(goBackReducer),
      ),
      transition(
        ACTION_TYPES.goBack,
        STATE.recommendedPlan,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.recommendedPlan,
        })),
        reduce(goBackReducer),
      ),
      /**
       ** Route: Continue/Next
       *
       ** Transitions to Checkout if
       ** - allPetsComplete: true
       */
      transition(
        ACTION_TYPES.toCheckout,
        STATE.checkout,
        guard(allPetsComplete),
        reduce((ctx, { payload }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          extras: payload.extras,
          pet: payload.pet,
          pets: payload.pets,
          flags: { ...ctx.flags, ...payload.flags },
        })),
      ),
    ),
    [STATE.deletePet]: invoke(
      async ctx => {
        return ctx.deletePetFromCart(ctx.pet_id || ctx.pet.id);
      },
      transition(
        'done',
        STATE.checkout,
        reduce((ctx, { data }) => ({
          ...ctx,
          stateKey: STATE.checkout,
          cart: data.cart,
          pets: data.pets,
          pet: data.pets[0],
          pet_id: data.pets[0].id,
        })),
      ),
      transition(
        'error',
        STATE.checkout,
        reduce(ctx => ({
          ...ctx,
          stateKey: STATE.checkout,
        })),
      ),
    ),
    [STATE.checkout]: final(),
  },
  createInitialContext,
);
